import {
  base64UrlToBytes,
  bytesToBase64Url,
  concatBytes,
  utf8ToBytes,
  type WrappedKey,
} from "@zeroclipboard/shared";
import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  exportRawPublic,
  generateX25519,
  hkdfSha256,
  importAesGcmKey,
  importRawX25519Public,
  randomBytes,
  x25519Shared,
} from "./primitives";

const GSK_WRAP_INFO = utf8ToBytes("ZeroClipboard/GSK-wrap/v1");
const SESSION_INFO = utf8ToBytes("ZeroClipboard/1-1/v1");
const EMPTY = new Uint8Array(0);

/**
 * ECIES-style wrap of a group key (GSK) to one member's X25519 public key.
 * An ephemeral key pair is used per wrap; the KEK is bound to both public keys.
 */
export async function wrapKeyForDevice(
  keyMaterial: Uint8Array,
  memberPublicRaw: Uint8Array
): Promise<WrappedKey> {
  const eph = await generateX25519();
  const ephRaw = await exportRawPublic(eph.publicKey);
  const memberPub = await importRawX25519Public(memberPublicRaw);
  const shared = await x25519Shared(eph.privateKey, memberPub);
  const salt = concatBytes(ephRaw, memberPublicRaw);
  const kek = await importAesGcmKey(
    await hkdfSha256(shared, salt, GSK_WRAP_INFO, 32)
  );
  const iv = randomBytes(12);
  const ct = await aesGcmEncrypt(kek, iv, keyMaterial);
  return {
    ephemeralPublicKey: bytesToBase64Url(ephRaw),
    nonce: bytesToBase64Url(iv),
    ciphertext: bytesToBase64Url(ct),
  };
}

/** Recover a wrapped key using our non-extractable private key. */
export async function unwrapKeyForSelf(
  wrapped: WrappedKey,
  selfPrivate: CryptoKey,
  selfPublicRaw: Uint8Array
): Promise<Uint8Array> {
  const ephRaw = base64UrlToBytes(wrapped.ephemeralPublicKey);
  const ephPub = await importRawX25519Public(ephRaw);
  const shared = await x25519Shared(selfPrivate, ephPub);
  const salt = concatBytes(ephRaw, selfPublicRaw);
  const kek = await importAesGcmKey(
    await hkdfSha256(shared, salt, GSK_WRAP_INFO, 32)
  );
  return aesGcmDecrypt(
    kek,
    base64UrlToBytes(wrapped.nonce),
    base64UrlToBytes(wrapped.ciphertext)
  );
}

/**
 * Direct 1-1 session key (section 6): sessionKey = HKDF(ECDH(selfPriv, peerPub)).
 * The salt binds both public keys in a canonical (order-independent) way so both
 * peers derive the identical key.
 */
export async function derive1to1SessionKey(
  selfPrivate: CryptoKey,
  selfPublicRaw: Uint8Array,
  peerPublicRaw: Uint8Array
): Promise<Uint8Array> {
  const peer = await importRawX25519Public(peerPublicRaw);
  const shared = await x25519Shared(selfPrivate, peer);
  const salt = canonicalConcat(selfPublicRaw, peerPublicRaw);
  return hkdfSha256(shared, salt, SESSION_INFO, 32);
}

/** Fresh 256-bit group key. */
export function generateGroupKey(): Uint8Array {
  return randomBytes(32);
}

function canonicalConcat(a: Uint8Array, b: Uint8Array): Uint8Array {
  return compareBytes(a, b) <= 0 ? concatBytes(a, b) : concatBytes(b, a);
}

function compareBytes(a: Uint8Array, b: Uint8Array): number {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    if (a[i]! !== b[i]!) return a[i]! - b[i]!;
  }
  return a.length - b.length;
}

export { EMPTY };
